import {
    BULLETPOINT,
    RELATED_LINK,
    STUB,
    VIDEO,
    EXTERNAL_TYPES,
    OVERRIDE_CATEGORY,
    MEDIA_TYPES,
} from "./constants";
import {
    FEATURED_STORIES_MAX_COUNT,
    TOP_STORIES_MAX_COUNT,
    HIGHLIGHTED_STORIES_MAX_COUNT,
} from "../../constants/content";
import CATEGORY_SLUGS from "../../constants/slugs/category";
import { SECTIONS } from "../../constants/sections";
import { VIDEO_CANONICAL_BASE_PATH } from "../../constants/videos";
import {
    removeUrlDomain,
    getLineupSlug,
    getCategorySlugOverride,
    getCategorySlug,
    getBaseSection,
} from "../path/library";
import { FEATURED_CONDENSED_KEY } from "../../pages/FlexibleLandingPage/constants";

export const bulletPointTypeMap = (obj) => {
    return { text: obj.text };
};

export const relatedLinksMap = (obj) => {
    return {
        key: key(obj),
        url: url(obj),
        external: external(obj),
        title: title(obj),
        flag: flag(obj),
    };
};

export const secondaryMap = (obj) => {
    return {
        url: url(obj),
        flag: flag(obj),
        external: external(obj),
        key: key(obj),
        title: title(obj),
        sourceId: obj?.item?.sourceId,
    };
};

export const createPackageComponents = (contentPackage) => {
    const primary = Array.isArray(contentPackage?.primary) && {
        primary: {
            bulletpoints: contentPackage.primary
                .filter((obj) => obj?.type === BULLETPOINT)
                .map(bulletPointTypeMap),
            relatedLinks: contentPackage.primary
                .filter((obj) => obj?.type === RELATED_LINK)
                .map(relatedLinksMap),
        },
    };
    const secondary = Array.isArray(contentPackage?.secondary) && {
        secondary: contentPackage.secondary.map(secondaryMap),
    };

    return [primary, secondary].reduce(
        (acc, obj) => (obj ? { ...acc, ...obj } : acc),
        {}
    );
};

export const title = (obj) => {
    return obj?.localHeadline || obj?.item?.title;
};

export const external = (obj) => {
    return obj?.item?.type === STUB || false;
};

export const url = (obj) => {
    let url = obj?.item?.typeAttributes?.url || "";
    if (obj?.item?.type === VIDEO && obj.item.sourceId) {
        url = `${VIDEO_CANONICAL_BASE_PATH}video/${obj.item.sourceId}`;
    }
    return url;
};

export const flag = (obj) => {
    return obj?.item?.typeAttributes?.flag ?? "";
};

export const key = (obj) => {
    return `${obj?.item?.sourceId}-${obj?.item?.updatedAt}`;
};

export const image = (obj) => {
    return obj?.item?.typeAttributes?.imageLarge || "";
};

export function getSlug(url) {
    if (url) {
        const slug = url.split("/").pop();
        const endIndex = slug.lastIndexOf("-");

        if (endIndex !== -1) {
            return slug.substring(0, endIndex);
        }
    }
    return "";
}

/**
 * Map transformed props to common link interface
 *
 * @param {Object} props (REQUIRED) item props to be used to determine link props.
 *
 * @returns {Object} props needed for link
 */
export function createLinkProps({
    sourceId,
    external,
    url,
    cardClickHandler,
} = {}) {
    return {
        "data-contentid": sourceId || "",
        external: external || false,
        url: url || "",
        onClick: cardClickHandler || (() => {}),
    };
}

/*
Following ID's are needed to references primary & secondary content for all landing page cards & reduce  verbosity.
Source card id's (PLPY or 9 reference) needed to reference card content pieces (flag, headline, description & meta)
and create aria-* relationships where needed.
flag & headline are essential to understand function & purpose where as description & metadata are complimentary info
that shouldn't be forced on non-visual users.
*/

export function createCardIDs(prefix, sourceId) {
    const cardId = `${prefix}-${sourceId}` || "";

    return {
        id: cardId,
        flagId: `f-${cardId}`,
        headlineId: `h-${cardId}`,
        descriptionId: `d-${cardId}`,
        metadataId: `m-${cardId}`,
    };
}

export function filterContentItem(contentItem) {
    const sectionList = contentItem.typeAttributes?.sectionList;
    const external = isItemExternal(contentItem);

    if (external !== true && (!sectionList || sectionList.length === 0)) {
        return false;
    }

    return true;
}

/**
 * Get departments from a story url
 * @param {String} url (REQUIRED)
 * @returns {Object} departments.
 */
export function getDepartmentsFromURL(url) {
    const sectionList = url.match(/\/(\w+)\//i).slice(1);
    const sectionLabels = sectionList.map(
        (path) => path.charAt(0).toUpperCase() + path.slice(1)
    );

    return {
        sectionList: sectionList,
        sectionLabels: sectionLabels,
    };
}

/**
 * Get the dept hierarchy for the url from the list of categories.
 *
 * @param {Array} departments ordered list of departments to use to build dept hierarchy.
 *
 * @returns {string} the department hierarchy for the item.
 */
export function getDepartmentHierarchy(departments) {
    if (!departments || departments === undefined || !departments.length) {
        return "";
    }

    const departmentHierarchy = departments.reduce(
        (previousValue, currentValue) => {
            let category = previousValue;

            if (currentValue !== undefined) {
                category += `/${currentValue}`;
            }

            return category;
        }
    );

    return departmentHierarchy;
}

/**
 * Given the item, decides if it is internal, or external content.
 *
 * @param {Object} item a URL - the content item to be examined.
 * @param {string} urlCategory - item categories, out of which the url is created
 *
 * @returns {boolean} 'true' if item is external, 'false' otherwise.
 */
export function isItemExternal(item = {}, urlCategory = null) {
    const isValidMediaType = item.sourceId && MEDIA_TYPES.includes(item.type);
    const isLiveStory = item.type === "livestory";

    if (isValidMediaType || isLiveStory) return false;

    const isExternalType = EXTERNAL_TYPES.includes(item.type);
    const isMainContentStub = item.components?.mainContent?.type === "stub";

    const hasUrlCategories = urlCategory && urlCategory.trim();

    const externalSponsoredContent =
        item.type === "sponsoredcontent" &&
        item.typeAttributes?.sponsor?.external === true;

    const isExternal =
        isExternalType ||
        isMainContentStub ||
        !hasUrlCategories ||
        externalSponsoredContent;

    return isExternal;
}

/**
 * Given the Search result item, decides if it is internal, or external content.
 * Decision is made by checking if the URL matches any contentlist category keys.
 *
 * @param {string} url - url that is returned from the search results.
 *
 * @returns {boolean} 'true' if item is external, 'false' otherwise.
 */

export function isSearchItemExternal(url = null) {
    if (/(gem.cbc.ca|index.html|index.php)/.test(url)) {
        return true;
    }

    let formatURL = removeUrlDomain(url).replace(/[^/]+$/, "");

    if (formatURL.slice(0, 1) !== "/") {
        formatURL = "/" + formatURL;
    }
    if (formatURL.slice(-1) !== "/") {
        formatURL = formatURL + "/";
    }
    const contentListCategories = Object.keys(CATEGORY_SLUGS);

    return !contentListCategories.some(
        (p) => formatURL.indexOf("/" + p + "/") === 0
    );
}

/**
 * Determine base section from full URL
 *
 * @param {string} url - url that is returned from the search results.
 *
 * @returns {string} - base section.
 */

export function getBaseSectionFromURL(url) {
    const urlCategories = removeUrlDomain(url);
    return getBaseSection(urlCategories);
}

/**
 * Show title from Shared Headline if exist
 * @param {object} item Polopoly element json
 * @return {string} shared title if exist
 */

export function getSharedTitle(item) {
    //apparently we also have to check for items like title = " "   ??? ¯\_(ツ)_/¯
    const shareHeadline =
        item.shareHeadline && item.shareHeadline.trim() !== ""
            ? item.shareHeadline
            : false;
    const title = item.title && item.title.trim() !== "" ? item.title : false;

    return title || shareHeadline || "";
}

/**
 * Split the list of items into Featured and Content list for Landing pages
 * @param {Array} itemList Polopoly element json
 * @param {bool} numTopStories Optional indicator whether the list has TopStories
 * @param {bool} hasHighlightedStories Optional indicator whether the list has HighlightedStories
 * @param {number} splitIndex Optional index for where the lists should be split
 * @return {object} The split lists
 */
export function split_content_lists(
    itemList,
    numTopStories = 0,
    hasHighlightedStories = true,
    splitIndex = 0
) {
    let featuredList = null;
    let contentList = null;
    let cutoffIndex = splitIndex ? splitIndex : FEATURED_STORIES_MAX_COUNT;

    if (numTopStories) {
        cutoffIndex = FEATURED_STORIES_MAX_COUNT + numTopStories;
    }

    if (hasHighlightedStories) {
        cutoffIndex = cutoffIndex + HIGHLIGHTED_STORIES_MAX_COUNT;
    }

    if (Array.isArray(itemList)) {
        featuredList = itemList.slice(0, cutoffIndex);
        contentList = itemList.slice(cutoffIndex, itemList.length);
    }

    return [featuredList, contentList];
}

/**
 * Check if the Featured Area needs to show the "Top Stories" layout.
 * The default number of top stories is defined in the config, but there are sections that override this value
 * @param {string} baseSection The current baseSection of the page
 * @return {number} The number of top stories for the given section
 */
export function getNumTopStories(baseSection) {
    const configSection = SECTIONS[baseSection];
    const numTopStories =
        configSection && configSection.customNumTopStories
            ? configSection.customNumTopStories
            : TOP_STORIES_MAX_COUNT;
    return numTopStories;
}

/**
 * Formats card headlines by taking under account contextual headlines
 * defined in Polopoly.
 * Contextual headlines are replacements to the default title for a story based on the section it is under.
 * @param {string} section The given section for which contextual headlines might be defined
 * @param {List} list The content list
 * @returns {void}
 */
export const formatHeadlines = (section, list) => {
    if (!(list instanceof Array) && !section) {
        return;
    }
    const slug = getLineupSlug(section);
    if (!slug) {
        return;
    }
    formatHeadLinesWithSlug(slug, list);
};

export const formatHeadLinesWithSlug = (slug, list) => {
    let i = list.length;
    let obj;
    let j;
    let ctx;
    while (i--) {
        obj = list[i];

        // GraphQL schema has eliminated typeAttributes, so fallback to REST API if needed
        obj =
            (Array.isArray(obj.contextualHeadlines) &&
                obj.contextualHeadlines.length > 0 &&
                obj.contextualHeadlines) ||
            (obj.typeAttributes && obj.typeAttributes.contextualHeadlines);

        if (!(obj instanceof Array)) {
            continue;
        }

        j = obj.length;

        while (j--) {
            ctx = obj[j];
            if (!ctx) {
                continue;
            }

            if (ctx.contextualLineupSlug === slug && ctx.headline) {
                list[i].title = ctx.headline;
                break;
            }
        }
    }
};
/**
 * Mainly used when retrieving widgets needed for a specific page
 * @param {object} obj The object containing the item we're interested in
 * @param {object} prop The item needed to be exctracted from the object
 * @returns {object} The requested item
 */
export const fetchFromObject = (obj, prop) => {
    if (typeof obj === "undefined" || !prop) {
        return false;
    }

    const notFoundIndex = -1;
    const index = prop ? prop.indexOf(".") : notFoundIndex;
    if (index > notFoundIndex) {
        return fetchFromObject(
            obj[prop.substring(0, index)],
            prop.substr(index + 1)
        );
    }
    return obj[prop];
};

/**
 * Check if our internal "cache" time has expired, e.g. hold objects in state for a certain time
 * @param {object} cacheTime Amount of time to cache
 * @param {object} now The current datetime
 * @param {object} before The previous datetime
 * @returns {boolean} A flag indicating whether to expire the content
 */
export const timeCacheExpired = (cacheTime, now, before) => {
    const minutes = 60;
    const seconds = 1000;

    //newTime = before + CACHE_TIME
    const newTime = before + cacheTime * minutes * seconds;
    return newTime <= now ? true : false;
};

export const filterDuplicateSourceIds = (contentList) => {
    return contentList.reduce((list, item) => {
        const id = item.sourceId;
        if (!list.find((item) => item.sourceId === id)) {
            list.push(item);
        }
        return list;
    }, []);
};

/**
 * Replaces the derivative value in an image string
 *
 * @param {string} image string
 * @returns {string}
 */

const isPolopolyDerivative = (imageUrl) => /_gen\/derivatives\//.test(imageUrl);

export const replaceWithLargerDerivative = (imageUrl) => {
    return isPolopolyDerivative(imageUrl)
        ? imageUrl.replace("_620", "_780")
        : imageUrl;
};

export const replaceWithSmallSquareDerivative = (imageUrl) => {
    return isPolopolyDerivative(imageUrl)
        ? imageUrl.replace(/\/([0-z]+_\d+)\//, "/square_220/")
        : imageUrl;
};

export const replaceWithSquareDerivative = (imageUrl) => {
    return isPolopolyDerivative(imageUrl)
        ? imageUrl.replace("/16x9_", "/square_")
        : imageUrl;
};

/**
 * Override the category slug if it's an override category
 *
 * @param {string} section the name of the selected section
 * @param {string} categorySlug the category slug for the section
 * @returns {string}
 */
export function maybeOverrideCategorySlug(section, categorySlug) {
    const categorySlugOverride = getCategorySlugOverride(section);
    if (categorySlugOverride !== null) {
        return categorySlugOverride;
    }
    // community widgets (community URLs) should always use categorySlug of empty-category(id 140)
    // this will allow us to pull only the number of stories in the polopoly queue regardless of itemCount
    return section.includes(OVERRIDE_CATEGORY.communityWidgetCategory)
        ? getCategorySlug(OVERRIDE_CATEGORY.override)
        : categorySlug;
}

/**
 * Given page data, checks if a native ad should be inserted into the content
 *
 * @param {Object} page object containing page data
 * @param {Object} sectionContent object containing relevant section data
 * @param {Object} sectionKey number for determining which section to feal with
 * @returns {boolean} 'true' if native ad should be inserted, 'false' otherwise.
 */
export const checkForNativeAd = (page, sectionContent, sectionKey) => {
    const layout = page.sections[sectionKey]?.main?.layout;

    // do not add native ads to any news page
    if (page.path && page.path.startsWith("news")) {
        return null;
    }

    // Native ads are not added to the "featured-condensed" layout
    return (
        sectionContent &&
        sectionContent.type === "lineup" &&
        layout !== FEATURED_CONDENSED_KEY
    );
};

/**
 * Count total cards on page load for each unique pubQ, needed until StartAt is available in API
 * @return {Object} { "pubqId": NumberOfCards } eg. { "1.779802": 29, "1.979547": 1 }
 */
export const calculateTotalCards = (sections) => {
    const countStorage = {};

    if (!sections) {
        return countStorage;
    }

    sections.forEach((section) => {
        Object.values(section).forEach((mainOrRight) => {
            if (mainOrRight.content?.length) {
                mainOrRight.content.forEach((item) => {
                    if (item.type === "lineup") {
                        const pubQueueId = item?.lineup?.pubQueueId;
                        countStorage[pubQueueId] = countStorage[pubQueueId]
                            ? countStorage[pubQueueId] + item.content.length
                            : item.content.length;
                    }
                });
            }
        });
    });
    return countStorage;
};

/**
 * This function replaces the card title/headline and flag label for a single content item
 * received from the GQL ContentItem query based on a contextual headline.
 * Contextual overrides are replacements to the default title and flag.label for a story based on the section (slug) it is under.
 * @param {string} slug The given section, lineup slug, or slug for which contextual overrides might be defined
 * @param {List} contextualHeadlines An array of contextual overrides for a single content item.
 * @returns {object | void} This function returns the new headline and/or flag label if the slug and contextualLineupSlug match or returns null.
 */

export const getContextualOverridesBySlug = (slug, contextualOverrides) => {
    if (!contextualOverrides?.length) return null;

    const override = contextualOverrides.find(
        ({ contextualLineupSlug }) => contextualLineupSlug === slug
    );

    if (!override) return null;
    return override;
};

export const convertImageDerivativesIntoSrcSet = (derivatives) => {
    if (!derivatives) return [];
    const srcset = [];
    Object.values(derivatives).forEach((value) => {
        if (!value?.fileurl || !value?.w) return;
        srcset.push(`${value.fileurl} ${value.w}w`);
    });
    return srcset;
};
